import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  ChevronLeft,
  Star,
  Clock,
  Trash2,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';
import NoImageTile from '@/components/NoImageTile';

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const fetchFavorites = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const removeFavorite = async (restaurantId) => {
    setRemoving(restaurantId);
    try {
      await axios.delete(`${API}/favorites/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.restaurant_id !== restaurantId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="favorites-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Favorites</h1>
            <p className="text-sm text-muted-foreground">
              {favorites.length} saved restaurant{favorites.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Favorites List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 flex gap-4">
                  <Skeleton className="w-24 h-24 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-1/3 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-4 w-1/4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
            <p className="text-muted-foreground mb-4">
              Save your favorite restaurants to quickly find them later
            </p>
            <Button onClick={() => navigate('/')} className="btn-pill">
              Browse Restaurants
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((fav) => (
              <Card
                key={fav.id}
                className="cursor-pointer card-hover"
                onClick={() => navigate(`/restaurant/${fav.restaurant_id}`)}
              >
                <CardContent className="p-4 flex gap-4">
                  {fav.restaurant_image ? (
                    <img
                      src={fav.restaurant_image}
                      alt={fav.restaurant_name}
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                  ) : (
                    <NoImageTile name={fav.restaurant_name} className="w-24 h-24 rounded-lg" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">{fav.restaurant_name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{fav.cuisine_type}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        {fav.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {fav.delivery_time_min}-{fav.delivery_time_max} min
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(fav.restaurant_id);
                    }}
                    disabled={removing === fav.restaurant_id}
                  >
                    {removing === fav.restaurant_id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
